import React, { useEffect, useMemo } from 'react';
import './PaymentMethod.css';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import {
  clearSecondAddress,
  loadSecondAddressByZipCode,
  paymentMethodChanged,
} from '../../../actions/quoting';
import {
  ACCOUNT_NUMBER_CHANGED,
  ACCOUNT_NUMBER_ERROR_CHANGED,
  ACCOUNT_TYPE_CHANGED,
  CARD_CVV_CHANGED,
  CARD_CVV_ERROR_CHANGED,
  CARD_NUMBER_CHANGED,
  CARD_NUMBER_ERROR_CHANGED,
  CONFIRM_ACCOUNT_NUMBER_CHANGED,
  CONFIRM_ACCOUNT_NUMBER_ERROR_CHANGED,
  CREDIT_CARD_TYPE_CHANGED,
  EXPIRATION_DATE_CHANGED,
  EXPIRATION_DATE_ERROR_CHANGED,
  NAME_ON_CARD_CHANGED,
  NAME_ON_CARD_ERROR_CHANGED,
  PAYMENT_METHOD_TYPE_CHANGE,
  ROUTING_NUMBER_CHANGED,
  ROUTING_NUMBER_ERROR_CHANGED,
  SAME_ADDRESS_FOR_BILLING_CHANGED,
  SECOND_ADDRESS_CITY_CHANGED,
  SECOND_ADDRESS_CITY_ERROR_CHANGED,
  SECOND_ADDRESS_STATE_ERROR_CHANGED,
  SECOND_ADDRESS_STREET_CHANGED,
  SECOND_ADDRESS_STREET_ERROR_CHANGED,
  SECOND_ADDRESS_STREET2_CHANGED,
  SECOND_ADDRESS_ZIP_CODE_CHANGED,
  SECOND_ADDRESS_ZIP_ERROR_CHANGED,
} from '../../../actions/types';
import visaCard from '../../../assets/visa-card.svg';
import visaCardSelected from '../../../assets/visa-card-selected.svg';
import discoverCard from '../../../assets/discover-card.svg';
import discoverCardSelected from '../../../assets/discover-card-selected.svg';
import americanExpressCard from '../../../assets/american-express-card.svg';
import americanExpressCardSelected
  from '../../../assets/american-express-card-selected.svg';
import masterCard from '../../../assets/mastercard.svg';
import masterCardSelected from '../../../assets/mastercard-selected.svg';
import TouchableDiv from '../../common/TouchableDiv';
import {
  AccountTypes,
  BILLING_CYCLE_TYPE,
  CreditCardType,
  PAYMENT_TYPE,
  PRODUCT_TYPE,
  CVV_CARD_LENGTH_MAX,
} from '../../../constants';
import {
  getCreditCardType,
  getProductType,
  isRoutingNumberValid,
  isValidCardCVV,
  isValidCardNumber,
  testOnlyNumber,
  isValidCardExpirationDate,
} from '../../../util';
import YourInfoSelect from './YourInfoSelect';
import YourInfoInput from './YourInfoInput';
import YourInfoInputCvv from './YourInfoInputCvv';
import { OneIncModal } from '../../common/OneIncModal';
import ill9 from '../../../assets/ills/ill-9.svg';

// eslint-disable-next-line no-unused-vars
import Switch from '../../common/Switch';

const REQUIRED_FIELD = 'This is a required field.';
const INVALID_CARD_NUMBER = 'Invalid card number or unsupported card network.';
const INVALID_ACCOUNT_NUMBER = 'Invalid account number.';
const INVALID_EXPIRATION_DATE = 'Invalid expiration date.';
const INVALID_CVV_CARD = 'Invalid CVV number.';
const INVALID_ROUTING_NUMBER = 'Invalid routing number.';
const INVALID_ZIPCODE_NUMBER = 'Invalid zip code number.';
const ACCOUNT_NUMBER_CONFIRM_ERROR = 'Account number does not match.';
const ACCOUNT_NUMBER_LENGTH_MAX = 30;
const CARD_NUMBER_SCORE_LENGTH = 19;
const AMEX_CARD_NUMBER_SCORE_LENGTH = 17;
const DINERS_CLUB_NUMBER_SCORE_LENGTH = 16;
const EXPIRATION_DATE_SLASH_LENGTH = 5;
const ZIP_CODE_LENGTH = 5;

const PaymentMethod = (props) => {
  const dispatch = useDispatch();
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const { isMonthlyPlan } = store;
  const {
    bankAccount,
    billingAddress,
    creditDebit,
    mailAddressSameAsPet,
    paymentMethodType,
  } = store.paymentMethod;

  const isBankAccount =
    paymentMethodType.value === PAYMENT_TYPE.bank.value;

  const handlePaymentMethodSelection = (paymentMethodSelection) => () => {
    if (paymentMethodSelection.value !== paymentMethodType.value) {
      dispatch(paymentMethodChanged(
        { type: PAYMENT_METHOD_TYPE_CHANGE, value: paymentMethodSelection },
      ));
    }
  };

  const maxCardLength = useMemo(() => {
    const crediType = getCreditCardType(creditDebit.cardNumber);

    if (crediType.type === CreditCardType.AmericanExpress.name) {
      return AMEX_CARD_NUMBER_SCORE_LENGTH;
    }

    if (crediType.type === CreditCardType.DinnersClub.name) {
      return DINERS_CLUB_NUMBER_SCORE_LENGTH;
    }

    return CARD_NUMBER_SCORE_LENGTH;
  }, [creditDebit.cardNumber]);

  function handleAccountTypeChange(value) {
    dispatch(paymentMethodChanged(
      { type: ACCOUNT_TYPE_CHANGED, value },
    ));
  }

  function handleRoutingNumberChange(event) {
    const { value } = event.target;
    const error = testOnlyNumber(value) ? '' : INVALID_ROUTING_NUMBER;

    if (!error) {
      dispatch(paymentMethodChanged(
        { type: ROUTING_NUMBER_ERROR_CHANGED, value: error },
      ));
      dispatch(paymentMethodChanged(
        { type: ROUTING_NUMBER_CHANGED, value },
      ));
    }
  }

  function handleRoutingNumberBlur() {
    const error = !isRoutingNumberValid(bankAccount.routingNumber)
      ? INVALID_ROUTING_NUMBER : '';
    dispatch(paymentMethodChanged(
      { type: ROUTING_NUMBER_ERROR_CHANGED, value: error },
    ));

    return error;
  }

  function handleAccountNumberChange(event) {
    const value = event.target.value.replace(/-/g, '');
    const error = testOnlyNumber(value) ? '' : INVALID_ACCOUNT_NUMBER;

    if (error) {
      return;
    }

    dispatch(paymentMethodChanged(
      { type: ACCOUNT_NUMBER_ERROR_CHANGED, value: error },
    ));

    dispatch(paymentMethodChanged(
      { type: ACCOUNT_NUMBER_CHANGED, value },
    ));

    if (bankAccount.accountNumberConfirmation) {
      const errorNumberConfirm = value
        !== bankAccount.accountNumberConfirmation
        ? ACCOUNT_NUMBER_CONFIRM_ERROR : '';

      dispatch(paymentMethodChanged(
        {
          type: CONFIRM_ACCOUNT_NUMBER_ERROR_CHANGED,
          value: errorNumberConfirm,
        },
      ));
    }
  }

  function handleAccountNumberBlur() {
    const valueFormated = bankAccount.accountNumber.trim();
    let error = '';

    if (!valueFormated) {
      error = REQUIRED_FIELD;
    }

    if (!testOnlyNumber(valueFormated)) {
      error = INVALID_ACCOUNT_NUMBER;
    }

    dispatch(paymentMethodChanged(
      { type: ACCOUNT_NUMBER_ERROR_CHANGED, value: error },
    ));

    return error;
  }

  function handleAccountNumberConfirmChange(event) {
    const { value } = event.target;
    const errorOnlyNumbers = testOnlyNumber(value)
      ? '' : ACCOUNT_NUMBER_CONFIRM_ERROR;

    if (errorOnlyNumbers) {
      return;
    }

    dispatch(paymentMethodChanged(
      { type: CONFIRM_ACCOUNT_NUMBER_CHANGED, value },
    ));

    const error = bankAccount.accountNumber !== value
      ? ACCOUNT_NUMBER_CONFIRM_ERROR : '';

    dispatch(paymentMethodChanged(
      { type: CONFIRM_ACCOUNT_NUMBER_ERROR_CHANGED, value: error },
    ));
  }

  function handleAccountNumberConfirmBlur() {
    const error =
      bankAccount.accountNumber !== bankAccount.accountNumberConfirmation
        ? ACCOUNT_NUMBER_CONFIRM_ERROR : '';
    dispatch(paymentMethodChanged(
      { type: CONFIRM_ACCOUNT_NUMBER_ERROR_CHANGED, value: error },
    ));

    return error;
  }

  function handleNameOnCardChange(event) {
    dispatch(paymentMethodChanged(
      { type: NAME_ON_CARD_CHANGED, value: event.target.value },
    ));
  }

  function handleNameOnCardBlur() {
    const valueFormated = creditDebit.nameOnCard.trim();
    dispatch(paymentMethodChanged(
      { type: NAME_ON_CARD_CHANGED, value: valueFormated },
    ));

    const error = !valueFormated ? REQUIRED_FIELD : '';
    dispatch(paymentMethodChanged(
      { type: NAME_ON_CARD_ERROR_CHANGED, value: error },
    ));

    return error;
  }

  function handleCardNumberChange(event) {
    const value = event.target.value.replace(/-/g, '');
    const error = testOnlyNumber(value) ? '' : INVALID_CARD_NUMBER;

    if (error) {
      return;
    }

    dispatch(paymentMethodChanged(
      { type: CARD_NUMBER_ERROR_CHANGED, value: error },
    ));

    let newCardNumber = value;

    const cardType = getCreditCardType(value).type;
    if ((cardType === CreditCardType.AmericanExpress.name
      || cardType === CreditCardType.DinnersClub.name)
      && value
      && value.length > 4
    ) {
      const startPart = value.substring(0, 4);
      const endPart = value.substring(4);
      newCardNumber = `${startPart}-`
        + `${endPart.match(new RegExp('.{1,6}', 'g')).join('-')}`;
    } else {
      newCardNumber = value && value.match(new RegExp('.{1,4}', 'g')).join('-');
    }

    dispatch(paymentMethodChanged(
      { type: CARD_NUMBER_CHANGED, value: newCardNumber },
    ));

    const typeCheck = value.substring(0, 2);
    let newCreditCardType = CreditCardType.Other;

    if (typeCheck >= 40 && typeCheck <= 49) {
      newCreditCardType = CreditCardType.Visa;
    } else if (typeCheck >= 51 && typeCheck <= 55) {
      newCreditCardType = CreditCardType.MasterCard;
    } else if ((typeCheck >= 60 && typeCheck <= 62)
      || (typeCheck === '64') || (typeCheck === '65')) {
      newCreditCardType = CreditCardType.Discover;
    } else if (typeCheck === '34' || typeCheck === '37') {
      newCreditCardType = CreditCardType.AmericanExpress;
    }

    dispatch(paymentMethodChanged(
      { type: CREDIT_CARD_TYPE_CHANGED, value: newCreditCardType },
    ));
  }

  function handleCardNumberBlur() {
    const valueFormated = creditDebit.cardNumber.trim().replace(/-/g, '');
    const cardType = getCreditCardType(valueFormated).type;

    let error = '';

    if (!valueFormated) {
      error = REQUIRED_FIELD;
    }

    if (!isValidCardNumber(valueFormated)
      || cardType === CreditCardType.DinnersClub.name) {
      error = INVALID_CARD_NUMBER;
    }

    dispatch(paymentMethodChanged(
      { type: CARD_NUMBER_ERROR_CHANGED, value: error },
    ));

    return error;
  }

  function handleBillingAddressStreetBlur() {
    let error = '';

    if (!mailAddressSameAsPet && !billingAddress.street.trim().length) {
      error = REQUIRED_FIELD;
    }

    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_STREET_ERROR_CHANGED,
      value: error,
    }));
  }

  function handleBillingAddressZipBlur() {
    let error = '';

    if (!mailAddressSameAsPet) {
      if (!billingAddress.zip.trim().length) {
        error = REQUIRED_FIELD;
      } else if (!testOnlyNumber(billingAddress.zip)
        || billingAddress.zip.length !== ZIP_CODE_LENGTH) {
        error = INVALID_ZIPCODE_NUMBER;
      } else if (billingAddress.zip.length === 5) {
        dispatch(paymentMethodChanged({
          type: SECOND_ADDRESS_CITY_ERROR_CHANGED,
          value: '',
        }));
        dispatch(loadSecondAddressByZipCode(billingAddress.zip));
      }
    }

    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_ZIP_ERROR_CHANGED,
      value: error,
    }));
  }

  function handleBillingAddressCityBlur() {
    let error = '';

    if (!mailAddressSameAsPet && !billingAddress.city.trim().length) {
      error = REQUIRED_FIELD;
    }

    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_CITY_ERROR_CHANGED,
      value: error,
    }));
  }

  function handleBillingAddressStateBlur() {
    let error = '';

    if (!mailAddressSameAsPet && !billingAddress.state.trim().length) {
      error = REQUIRED_FIELD;
    }

    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_STATE_ERROR_CHANGED,
      value: error,
    }));
  }

  function handleExpirationDateChange(event) {
    const value = event.target.value.replace(/\//g, '');
    const error = testOnlyNumber(value) ? '' : INVALID_EXPIRATION_DATE;

    if (error) {
      return;
    }

    dispatch(paymentMethodChanged(
      { type: EXPIRATION_DATE_ERROR_CHANGED, value: error },
    ));

    let newExpirationDate = value;

    if (value.length > 2) {
      newExpirationDate =
        `${value.substring(0, 2)}/${value.substring(2, value.length)}`;
    }

    dispatch(paymentMethodChanged(
      { type: EXPIRATION_DATE_CHANGED, value: newExpirationDate },
    ));
  }

  function handleExpirationDateBlur() {
    const valueFormated = creditDebit.expirationDate.trim().replace(/\//g, '');
    let error = '';

    if (!valueFormated) {
      error = REQUIRED_FIELD;
    }

    if (!isValidCardExpirationDate(valueFormated)) {
      error = INVALID_EXPIRATION_DATE;
    }

    dispatch(paymentMethodChanged(
      { type: EXPIRATION_DATE_ERROR_CHANGED, value: error },
    ));

    return error;
  }

  function handleCVVCardChanged(event) {
    const { value } = event.target;
    const error = testOnlyNumber(value) ? '' : INVALID_CVV_CARD;

    if (!error) {
      dispatch(paymentMethodChanged({
        type: CARD_CVV_ERROR_CHANGED,
        value: error,
      }));
      dispatch(paymentMethodChanged({
        type: CARD_CVV_CHANGED,
        value: event.target.value,
      }));
    }
  }

  function handleCVVCardBlur() {
    const valueFormated = creditDebit.cardCvv.trim();
    dispatch(paymentMethodChanged(
      { type: CARD_CVV_CHANGED, value: valueFormated },
    ));
    let error = '';

    if (!valueFormated) {
      error = REQUIRED_FIELD;
    }

    if (!isValidCardCVV(valueFormated)) {
      error = INVALID_CVV_CARD;
    }

    dispatch(paymentMethodChanged({
      type: CARD_CVV_ERROR_CHANGED,
      value: error,
    }));

    return error;
  }

  function getMontlyFeeLabel() {
    const { monthly } = BILLING_CYCLE_TYPE;
    const { InsuranceProductFee } = store.data.insuranceProduct;
    const fee = InsuranceProductFee
      .find((item) => item.InsuranceFee.BillingCycleType === monthly
        && item.InsuranceFee.PaymentType === PAYMENT_TYPE.creditCard.value);

    const productId = store.data.insuranceProduct.Id;
    const productType = getProductType(productId);

    if (!fee || !isMonthlyPlan || productType === PRODUCT_TYPE.Unknown
      || (productType === PRODUCT_TYPE.FPI && isBankAccount)) {
      return null;
    }

    const { InsuranceFee } = fee;

    return (
      <span className="Payment-method-credit-card-title">
        {InsuranceFee.DisplayText.replace('{0}', InsuranceFee.Amount)}
      </span>
    );
  }

  // TODO one inc not neccessary
  // eslint-disable-next-line no-unused-vars
  function handleSameAddressForBillingSwitch() {
    dispatch(paymentMethodChanged({
      type: SAME_ADDRESS_FOR_BILLING_CHANGED,
    }));
    dispatch(clearSecondAddress());
  }

  // TODO one inc not neccessary
  // eslint-disable-next-line no-unused-vars
  function renderPaymentMethod() {
    if (paymentMethodType.value === PAYMENT_TYPE.bank.value) {
      return (
        <div className="Payment-method-bank-account-container">
          <div className="Payment-method-account-row-title">
            {getMontlyFeeLabel()}
          </div>

          <div className="Payment-method-account-row">
            <YourInfoSelect
              onChange={handleAccountTypeChange}
              options={AccountTypes}
              paddingRight
              title="Account Type"
              value={bankAccount.accountType}
            />

            <YourInfoInput
              error={bankAccount.routingNumberError}
              onBlur={handleRoutingNumberBlur}
              onChange={handleRoutingNumberChange}
              title="Routing Number"
              value={bankAccount.routingNumber}
            />
          </div>

          <div className="Payment-method-account-row">
            <YourInfoInput
              error={bankAccount.accountNumberError}
              maxLength={ACCOUNT_NUMBER_LENGTH_MAX}
              onBlur={handleAccountNumberBlur}
              onChange={handleAccountNumberChange}
              paddingRight
              title="Account Number"
              value={bankAccount.accountNumber}
            />

            <YourInfoInput
              error={bankAccount.accountNumberConfirmationError}
              onBlur={handleAccountNumberConfirmBlur}
              onChange={handleAccountNumberConfirmChange}
              title="Confirm Account Number"
              value={bankAccount.accountNumberConfirmation}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="Payment-method-credit-card-container">
        {getMontlyFeeLabel()}

        <div className="Payment-method-credit-card-cards-container">
          <img
            alt="Visa card"
            className="Payment-method-credit-card-card"
            src={creditDebit.creditCardType.value
              === CreditCardType.Visa.value
              ? visaCardSelected : visaCard}
          />

          <img
            alt="Mastercard"
            className="Payment-method-credit-card-card"
            src={creditDebit.creditCardType.value
              === CreditCardType.MasterCard.value
              ? masterCardSelected : masterCard}
          />

          <img
            alt="Discover card"
            className="Payment-method-credit-card-card"
            src={creditDebit.creditCardType.value
              === CreditCardType.Discover.value
              ? discoverCardSelected : discoverCard}
          />

          <img
            alt="American express card"
            src={creditDebit.creditCardType.value
              === CreditCardType.AmericanExpress.value
              ? americanExpressCardSelected : americanExpressCard}
          />
        </div>

        <div className="Payment-method-credit-card-inputs-container">
          <div className="Payment-method-account-row">
            <YourInfoInput
              error={creditDebit.nameOnCardError}
              onBlur={handleNameOnCardBlur}
              onChange={handleNameOnCardChange}
              paddingRight
              title="Name on Card"
              value={creditDebit.nameOnCard}
            />

            <YourInfoInput
              error={creditDebit.cardNumberError}
              maxLength={maxCardLength}
              onBlur={handleCardNumberBlur}
              onChange={handleCardNumberChange}
              title="Card Number"
              value={creditDebit.cardNumber}
            />
          </div>

          <div className="Payment-method-account-row">
            <YourInfoInput
              error={creditDebit.expirationDateError}
              maxLength={EXPIRATION_DATE_SLASH_LENGTH}
              onBlur={handleExpirationDateBlur}
              onChange={handleExpirationDateChange}
              paddingRight
              placeholder="MM / YY"
              title="Expiration"
              value={creditDebit.expirationDate}
            />

            <YourInfoInputCvv
              cvvCardLength={CVV_CARD_LENGTH_MAX}
              error={creditDebit.cardCvvError}
              onBlur={handleCVVCardBlur}
              onChange={handleCVVCardChanged}
              title="CVV"
              value={creditDebit.cardCvv}
            />
          </div>
        </div>
      </div>
    );
  }

  function handleStreetAddressInputChange(event) {
    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_STREET_CHANGED,
      value: event.target.value,
    }));
  }

  function handleStreetAddress2InputChange(event) {
    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_STREET2_CHANGED,
      value: event.target.value,
    }));
  }

  function handleZipCodeInputChange(event) {
    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_ZIP_CODE_CHANGED,
      value: event.target.value,
    }));
  }

  function handleCityInputChange(event) {
    dispatch(paymentMethodChanged({
      type: SECOND_ADDRESS_CITY_CHANGED,
      value: event.target.value,
    }));
  }

  // TODO one inc not neccessary
  // eslint-disable-next-line no-unused-vars
  function renderOtherBillingAddressFields() {
    if (!mailAddressSameAsPet) {
      return (
        <div className="Payment-method-credit-card-inputs-container">
          <div className="Payment-method-account-row">
            <YourInfoInput
              autoComplete="chrome-off"
              error={billingAddress.streetError}
              onBlur={handleBillingAddressStreetBlur}
              onChange={handleStreetAddressInputChange}
              paddingRight
              placeholder="Billing Address"
              title="Street Address"
              value={billingAddress.street}
            />

            <YourInfoInput
              autoComplete="chrome-off"
              error={billingAddress.street2Error}
              onChange={handleStreetAddress2InputChange}
              placeholder="Optional"
              title="Street Address 2"
              value={billingAddress.street2}
            />
          </div>

          <div className="Payment-method-account-row">
            <YourInfoInput
              autoComplete="chrome-off"
              disabled={store.paymentMethod.loadingSecondAddress}
              error={billingAddress.zipError}
              loading={store.paymentMethod.loadingSecondAddress}
              maxLength={ZIP_CODE_LENGTH}
              onBlur={handleBillingAddressZipBlur}
              onChange={handleZipCodeInputChange}
              paddingRight
              title="Zip Code"
              type="number"
              value={billingAddress.zip}
            />

            <YourInfoInput
              autoComplete="chrome-off"
              error={billingAddress.cityError}
              onBlur={handleBillingAddressCityBlur}
              onChange={handleCityInputChange}
              paddingRight
              title="City"
              value={billingAddress.city}
            />

            <YourInfoInput
              disabled
              error={billingAddress.stateError}
              onBlur={handleBillingAddressStateBlur}
              title="State"
              value={billingAddress.state}
            />
          </div>
        </div>
      );
    }

    return null;
  }

  // WIP purchase testing
  function navigateToPaymentMethod() {
    // const url = '/quoting/getQuote/paymentMethod/';
    // history.push(url);
  }

  function handleContinueToPaymentClick() {
    if (isBankAccount) {
      const routingNumberError = handleRoutingNumberBlur();
      const accountNumberError = handleAccountNumberBlur();
      const accountNumberConfirmationError = handleAccountNumberConfirmBlur();

      if (!bankAccount.routingNumberError && !routingNumberError
        && !bankAccount.accountNumberError && !accountNumberError
        && !bankAccount.accountNumberConfirmationError
        && !accountNumberConfirmationError) {
        navigateToPaymentMethod();
      }
    } else {
      const nameOnCardError = handleNameOnCardBlur();
      const cardNumberError = handleCardNumberBlur();
      const expirationDateError = handleExpirationDateBlur();
      const cardCvvError = handleCVVCardBlur();

      if (!creditDebit.nameOnCardError && !nameOnCardError
        && !creditDebit.cardNumberError && !cardNumberError
        && !creditDebit.expirationDateError && !expirationDateError
        && !creditDebit.cardCvvError && !cardCvvError) {
        navigateToPaymentMethod();
      }
    }

    if (!mailAddressSameAsPet) {
      handleBillingAddressStreetBlur();
      handleBillingAddressZipBlur();
      handleBillingAddressCityBlur();
      handleBillingAddressStateBlur();
    }
  }

  useEffect(() => {
    if (props.validatePaymentMethod > 0) {
      handleContinueToPaymentClick();
    }
  }, [props.validatePaymentMethod]);

  // TODO one inc not neccessary
  // eslint-disable-next-line no-unused-vars
  const shouldBeRenderPaymentMethodSwich = () => (
    <div className="Payment-method-type-container">
      <TouchableDiv
        ariaProps={{
          'aria-label': 'Bank account',
          title: 'Banck account',
        }}
        className="Payment-method-type-left"
        id="paymentMethodBankAccount"
        onClick={handlePaymentMethodSelection(PAYMENT_TYPE.bank)}
      >
        <span
          className={`Payment-method-type-${isBankAccount
            ? 'selected' : 'unselected'}`}
        >
          Bank Account
        </span>
      </TouchableDiv>

      <TouchableDiv
        ariaProps={{
          'aria-label': 'Credit or debit card',
          title: 'Credit or debit card',
        }}
        className="Payment-method-type-right"
        id="paymentMethodCreditCard"
        onClick={handlePaymentMethodSelection(PAYMENT_TYPE.creditCard)}
      >
        <span
          className={`Payment-method-type-${!isBankAccount
            ? 'selected' : 'unselected'}`}
        >
          Credit or Debit
        </span>
      </TouchableDiv>
    </div>
  );

  return (
    <div className="Payment-method-container" id="paymentMethod">
      <h3 className="Payment-method-title">Payment Method</h3>

      <OneIncModal />

      <img
        alt=""
        aria-hidden
        className="Ill-image Your-info-image-4 No-mobile No-tablet"
        src={ill9}
      />
    </div>
  );
};

export default PaymentMethod;
